import React, { type ReactNode, Fragment } from 'react'
import styled from 'styled-components'

import type { FragmentComponent } from '@/types/graphql'

import { BREAKPOINTS } from '@/components/ds/breakpoints'

import { antonio } from '@/lib/fonts'

type AntonioAlignment = 'left' | 'center' | 'right'

export type AntonioTextBlockRecord = {
  __typename: 'AntonioTextRecord'
  id: string
  text: string
  fontSize?: number
  alignment?: AntonioAlignment
}

const fragment = `
  fragment AntonioTextBlockFragment on AntonioTextRecord {
    __typename
    id
    text
    fontSize
    alignment
  }
`

const StyledAntonioWrapper = styled.div<{ $fontSize: number; $alignment: AntonioAlignment }>`
  font-family: ${antonio.style.fontFamily}, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  text-align: ${({ $alignment }) => $alignment};
  color: var(--color-surface-navy-bold);
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
  line-height: 1.03;
  letter-spacing: -0.02em;

  @media screen and (max-width: ${BREAKPOINTS.m}) {
    font-size: ${({ $fontSize }) => `${$fontSize / 2}px`};
    text-align: center;
  }

  && strong,
  && b {
    font-family: ${antonio.style.fontFamily}, sans-serif !important;
    font-weight: 700;
    color: var(--color-surface-yellow-bold);
  }
`

const renderText = (text: string): ReactNode[] => {
  const parts: ReactNode[] = []
  const boldRegex = /\*\*([^*\n]+?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  const pushWithLineBreaks = (chunk: string, keyPrefix: string) => {
    chunk.split('\n').forEach((line, i, arr) => {
      if (line) parts.push(<Fragment key={`${keyPrefix}-${i}`}>{line}</Fragment>)
      if (i < arr.length - 1) parts.push(<br key={`${keyPrefix}-br-${i}`} />)
    })
  }

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushWithLineBreaks(text.slice(lastIndex, match.index), `t-${lastIndex}`)
    }
    parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>)
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    pushWithLineBreaks(text.slice(lastIndex), `t-${lastIndex}`)
  }

  return parts
}

export const AntonioTextBlock: FragmentComponent<{}, AntonioTextBlockRecord> = ({ record }) => (
  <StyledAntonioWrapper
    className={antonio.className}
    $fontSize={record.fontSize || 66}
    $alignment={record.alignment ?? 'center'}
  >
    {renderText(record.text)}
  </StyledAntonioWrapper>
)

AntonioTextBlock.fragment = fragment
AntonioTextBlock.recordName = 'AntonioTextRecord'
